import type { FieldType } from '@kizen/api';

import { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  useContactCategories,
  useContactFields,
  useCategoriesQuery,
  useFieldsQuery,
} from 'queries/models/custom-objects';
import { useSelector } from 'react-redux';
import { getChosenBusiness } from 'store/authentication/selectors';
import { isMergeFieldUnescaped } from 'utility/merge-fields';

type Category = {
  id: string;
  meta: Record<string, any>;
  name: string;
  order: number;
};

type FieldLike = {
  category: string;
  displayName: string;
  fieldType: FieldType;
  id: string;
  isHidden: boolean;
  meta?: Record<string, any>;
  name: string;
};

export type MergeField = {
  label: string;
  relationship: string;
  unescaped?: boolean;
};

export type CategorizedMergeField = {
  label: string;
  items: MergeField[];
  key: string;
};

export type BusinessVariable = {
  id: string;
  name: string;
  api_name?: string;
  value_string: string;
};

export const isCategorizedMergeFieldList = (
  list: any[]
): list is CategorizedMergeField[] => {
  return list[0] && typeof list[0] === 'object' && 'items' in list[0];
};

export const getTeamMemberMergeFields = (t: TFunction): MergeField[] => [
  {
    label: t('Team Member First Name'),
    relationship: 'team_member.first_name',
  },
  {
    label: t('Team Member Last Name'),
    relationship: 'team_member.last_name',
  },
  {
    label: t('Team Member Email'),
    relationship: 'team_member.email',
  },
  {
    label: t('Team Member Phone'),
    relationship: 'team_member.phone',
  },
  {
    label: t('Team Member Signature'),
    relationship: 'team_member.signature',
  },
];

export const getBusinessMergeFields = (
  t: TFunction,
  businessVariables: BusinessVariable[] = [],
  excludeBusinessFields: boolean = false
): MergeField[] =>
  (excludeBusinessFields
    ? []
    : [
        {
          label: t('Business Name'),
          relationship: 'business.name',
        },
        {
          label: t('Business Notification Email'),
          relationship: 'business.reply_to_email',
        },
        {
          label: t('Business Phone'),
          relationship: 'business.phone',
        },
        {
          label: t('Business Street Address'),
          relationship: 'business.street_address',
        },
        {
          label: t('Business Country'),
          relationship: 'business.country',
        },
        {
          label: t('Business Country Code'),
          relationship: 'business.country_code',
        },
        {
          label: t('Business State/Province'),
          relationship: 'business.state',
        },
        {
          label: t('Business State/Province Code'),
          relationship: 'business.state_code',
        },
        {
          label: t('Business City'),
          relationship: 'business.city',
        },
        {
          label: t('Business Zip/Postal Code'),
          relationship: 'business.postal_code',
        },
        {
          label: t('Business Primary Name'),
          relationship: 'business.primary_marketing_contact_name',
        },
        {
          label: t('Business Primary Email'),
          relationship: 'business.primary_marketing_contact_email',
        },
      ]
  ).concat(
    businessVariables.map(({ name, api_name }) => ({
      label: name,
      relationship: `business_variable.${api_name}`,
    }))
  );

const mapFieldOption = (
  field: FieldLike,
  createRelationship: (field: FieldLike) => string
): MergeField => {
  const option: MergeField = {
    label: field.displayName,
    relationship: createRelationship(field),
  };

  if (isMergeFieldUnescaped(field)) {
    option.unescaped = true;
  }

  return option;
};

export const filterMergeField =
  (search: string) => (mergeField: MergeField) => {
    return mergeField.label.toLowerCase().includes(search.trim().toLowerCase());
  };

const buildMergeFields = (
  fields: FieldLike[],
  categories: Category[],
  createRelationship: (field: FieldLike) => string
): CategorizedMergeField[] => {
  const categorizedFields = fields.reduce<Map<string, MergeField[]>>(
    (acc, field) => {
      if (!acc.has(field.category)) {
        acc.set(field.category, []);
      }

      acc.get(field.category)!.push(mapFieldOption(field, createRelationship));

      return acc;
    },
    new Map<string, MergeField[]>()
  );

  const mergeFields: CategorizedMergeField[] = categories.map(
    (category: Category) => {
      return {
        label: category.name,
        items: categorizedFields.get(category.id) ?? [],
        key: category.id,
      };
    }
  );

  return mergeFields;
};

export const useBusinessMergeFields = ({
  listHeaders = false,
  forSelectOptions = false,
} = {}) => {
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);

  return useMemo(() => {
    let mergeFields = getBusinessMergeFields(
      t,
      chosenBusiness.business_variables
    );

    if (forSelectOptions) {
      mergeFields = mergeFields.map((mf) => {
        return {
          ...mf,
          value: mf.relationship,
        };
      });
    }

    if (listHeaders) {
      return [{ label: t('Business'), items: mergeFields }];
    }

    return mergeFields;
  }, [chosenBusiness, listHeaders, forSelectOptions, t]);
};

export const useTeamMemberMergeFields = ({
  listHeaders = false,
  forSelectOptions = false,
} = {}) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const mergeFields = getTeamMemberMergeFields(t);

    if (forSelectOptions) {
      return mergeFields.map((mf) => ({ ...mf, value: mf.relationship }));
    }

    if (listHeaders) {
      return [{ label: t('Team Member'), items: mergeFields }];
    }

    return mergeFields;
  }, [listHeaders, forSelectOptions, t]);
};

/**
 * This is the original hook for email merge fields which use the string literal 'contact' along with
 * the field's name property. All other merge fields use {@link useObjectMergeFields} which use the
 * object and field ids.
 */
export const useMergeFields = ({
  enabled = true,
  listHeaders = false,
  mergeFields,
}: {
  enabled?: boolean;
  listHeaders?: boolean;
  mergeFields?: MergeField[] | CategorizedMergeField[];
} = {}) => {
  const chosenBusiness = useSelector(getChosenBusiness);
  const { data: contactFields, isLoading: loadingFields } = useContactFields(
    enabled && !mergeFields
  );
  const { data: contactCategories, isLoading: loadingCategories } =
    useContactCategories(enabled && !mergeFields);
  const { t } = useTranslation();

  const data = useMemo<MergeField[] | CategorizedMergeField[]>(() => {
    if (mergeFields) {
      return mergeFields;
    }

    if (!enabled) {
      return [];
    }

    if (!Array.isArray(contactFields)) {
      return [];
    }

    if (!listHeaders) {
      return contactFields
        .map((field) => mapFieldOption(field, (x) => `contact.${x.name}`))
        .concat(getTeamMemberMergeFields(t))
        .concat(getBusinessMergeFields(t, chosenBusiness.business_variables));
    }

    if (!Array.isArray(contactCategories)) {
      return [];
    }

    const categorizedMergeFields = buildMergeFields(
      contactFields,
      contactCategories,
      (field) => `contact.${field.name}`
    );

    return categorizedMergeFields
      .concat({
        label: t('Team Member'),
        key: 'team_member',
        items: getTeamMemberMergeFields(t),
      })
      .concat({
        label: t('Business'),
        key: 'business',
        items: getBusinessMergeFields(t, chosenBusiness.business_variables),
      });
  }, [
    contactFields,
    contactCategories,
    enabled,
    listHeaders,
    mergeFields,
    t,
    chosenBusiness.business_variables,
  ]);

  return { data, isLoading: loadingCategories || loadingFields };
};

export const useObjectMergeFields = (
  id: string,
  objectName: string,
  objectType: Parameters<typeof useFieldsQuery>['0']['objectType']
) => {
  const { data: fields, isLoading: modelLoading } = useFieldsQuery(
    { id, objectType },
    Boolean(id) && Boolean(objectType)
  );
  const { data: categories = [], isLoading: loadingCategories } =
    useCategoriesQuery({ id, objectType }, Boolean(id) && Boolean(objectType));

  const data = useMemo<CategorizedMergeField[]>(() => {
    if (!fields) {
      return [];
    }
    return buildMergeFields(
      fields.filter((field: FieldLike) => !field.isHidden),
      categories,
      (field) => `${objectName}.${field.name}`
    );
  }, [fields, categories, objectName]);

  return { data, isLoading: modelLoading || loadingCategories };
};
